import { SpecPackage, SpecDocumentNode } from '../types/spec';
import { PersonaRole } from '../stores/useUIStore';

export interface RecommendedDoc {
  path: string;
  title: string;
  importance: 'Essential' | 'Recommended' | 'Reference';
  plainSummary: string;
}

export interface RoleExplanation {
  role: PersonaRole;
  roleTitle: string;
  tagline: string;
  whatItBuilds: string;
  businessImpact: string;
  keyCapabilities: string[];
  recommendedDocs: RecommendedDoc[];
}

export class ExplainerService {
  public static synthesizeExplanation(pkg: SpecPackage, role: PersonaRole): RoleExplanation {
    const pkgName = pkg.name || 'Specification Package';
    const totalDocs = pkg.documents.length;
    const totalTasks = pkg.taskMatrix.length;
    const completedTasks = pkg.taskMatrix.filter(t => t.status === 'completed').length;
    const progressPct = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;

    // Helper matcher
    const findDoc = (keywords: string[]) =>
      pkg.documents.find(d => keywords.some(k => d.path.toLowerCase().includes(k) || d.name.toLowerCase().includes(k) || (d.meta?.title && d.meta.title.toLowerCase().includes(k))));

    const visionDoc = findDoc(['vision', '01-']);
    const prdDoc = findDoc(['prd', 'product', '02-']);
    const trdDoc = findDoc(['trd', 'technical-req', '03-']);
    const archDoc = findDoc(['arch', 'system', '04-']);
    const designDoc = findDoc(['ui', 'ux', 'design', '05-']);
    const codingDoc = findDoc(['coding', 'standards', '14-']) || findDoc(['coding', 'standards']);
    const tasksDoc = findDoc(['task', 'phase', 'roadmap', '12-', '13-']);

    switch (role) {
      case 'executive':
        return {
          role: 'executive',
          roleTitle: 'Executive & Business Sponsor',
          tagline: 'High-Level Strategic Briefing, Business Outcomes & Investment ROI',
          whatItBuilds: `${pkgName} is a production software system engineered to solve core operational bottlenecks through automated, high-reliability execution. This specification package contains a complete software blueprint across ${totalDocs} specification modules and ${totalTasks} tracking milestones (${progressPct}% completed to date).`,
          businessImpact: `Deploying ${pkgName} delivers automated workflow execution, eliminates manual overhead, and enforces enterprise-grade security and reliability compliance across multi-tenant production operations.`,
          keyCapabilities: [
            `Automated core business logic execution with zero human intervention`,
            `Enterprise auditability with 100% durable event telemetry`,
            `Scalable Cloud infrastructure architecture designed for multi-region uptime`,
            `Governance roadmap tracking ${totalTasks} delivery milestones`
          ],
          recommendedDocs: this.buildRecommendedDocs([
            { doc: visionDoc, defaultTitle: 'Vision Statement', imp: 'Essential', summary: 'Outlines business positioning, problem statement, ROI goals, and product pillars.' },
            { doc: prdDoc, defaultTitle: 'Product Requirements (PRD)', imp: 'Essential', summary: 'Summarizes high-level functional scope, target personas, and key business deliverables.' },
            { doc: tasksDoc, defaultTitle: 'Master Phase Plan & Roadmap', imp: 'Recommended', summary: 'Provides executive milestone timeline, delivery budget, and task completion metrics.' }
          ], pkg.documents)
        };

      case 'po':
        return {
          role: 'po',
          roleTitle: 'Product Owner & Manager',
          tagline: 'Feature Scope, User Stories, Deliverable Milestones & Task Tracking',
          whatItBuilds: `${pkgName} delivers a user-centric software package featuring structured APIs, reactive workflow interfaces, and milestone-governed implementation tasks. It provides a complete product backlog mapped across ${totalTasks} explicit task markers.`,
          businessImpact: `Enables product teams to manage feature prioritization, track real-time milestone execution, and align developer deliverables directly against human product requirements.`,
          keyCapabilities: [
            `Comprehensive functional requirement coverage (FR-1 through FR-N)`,
            `Structured user story journeys for lead architects, senior developers, and product leads`,
            `5-state task progress tracking (Open, In-Progress, Completed, Blocked, Cancelled)`,
            `Milestone-based release criteria and acceptance guidelines`
          ],
          recommendedDocs: this.buildRecommendedDocs([
            { doc: prdDoc, defaultTitle: 'Product Requirements (PRD)', imp: 'Essential', summary: 'Defines functional requirements, user scenarios, and feature acceptance criteria.' },
            { doc: tasksDoc, defaultTitle: 'Master Task Matrix & Phase Plan', imp: 'Essential', summary: 'Tracks real-time task status breakdown across 5 execution states.' },
            { doc: visionDoc, defaultTitle: 'Vision Statement', imp: 'Recommended', summary: 'Defines core product goals, target user scenarios, and scope boundaries.' },
            { doc: designDoc, defaultTitle: 'UI/UX Design Specification', imp: 'Recommended', summary: 'Specifies visual design tokens, screen layouts, and user interface workflows.' }
          ], pkg.documents)
        };

      case 'architect':
        return {
          role: 'architect',
          roleTitle: 'Lead Architect & Systems Engineer',
          tagline: 'C4 Models, Subsystem Topology, Data Schemas & Technical Constraints',
          whatItBuilds: `${pkgName} is a multi-tier technical architecture comprising edge routing proxies, off-thread Web Worker execution pools, durable storage engines, and encrypted credential vaults. It defines precise component boundaries and data flow contracts.`,
          businessImpact: `Establishes sub-50ms latency performance budgets, strict data privacy isolation, and robust failover guarantees for enterprise deployment.`,
          keyCapabilities: [
            `C4 Container and Component schematic models with interactive Mermaid visual renderings`,
            `Strict database schemas, API protocol bindings, and data tenancy isolation models`,
            `Zero-trust Web Crypto AES-256 security boundaries and key vault integration`,
            `Off-thread worker thread execution architecture for heavy computation`
          ],
          recommendedDocs: this.buildRecommendedDocs([
            { doc: archDoc, defaultTitle: 'System Architecture', imp: 'Essential', summary: 'Details C4 container diagrams, subsystem interaction contracts, and edge proxy topology.' },
            { doc: trdDoc, defaultTitle: 'Technical Requirements (TRD)', imp: 'Essential', summary: 'Specifies protocol choices, encryption standards, performance budgets, and error boundaries.' },
            { doc: designDoc, defaultTitle: 'Visual Assets & Schematics', imp: 'Recommended', summary: 'Contains SVG architecture diagrams and component interaction sequences.' },
            { doc: codingDoc, defaultTitle: 'Coding Standards', imp: 'Reference', summary: 'Defines strict TypeScript guidelines, custom AppError hierarchy, and linting rules.' }
          ], pkg.documents)
        };

      case 'developer':
      default:
        return {
          role: 'developer',
          roleTitle: 'Senior Developer & Implementer',
          tagline: 'Coding Standards, Setup Guide, API Contracts & Implementation Tasks',
          whatItBuilds: `${pkgName} is a modular TypeScript/React application built with strict compilation rules (\`strict: true\`), zero \`any\` types, and custom \`AppError\` exception handlers. It provides clear file locations and explicit code implementation guidance.`,
          businessImpact: `Ensures 100% type safety, >90% automated test coverage with Vitest and Playwright, and clean code maintainability.`,
          keyCapabilities: [
            `Strict TypeScript typing rules and custom exception hierarchy definitions`,
            `Clean code block examples with 1-click copy for fast implementation`,
            `Deep-linked task matrix connecting directly to line-numbered markdown specifications`,
            `Comprehensive Vitest unit test harnesses and Playwright E2E visual regression setup`
          ],
          recommendedDocs: this.buildRecommendedDocs([
            { doc: codingDoc, defaultTitle: 'Coding & Engineering Standards', imp: 'Essential', summary: 'Mandatory coding guidelines, tsconfig rules, AppError hierarchy, and lint rules.' },
            { doc: tasksDoc, defaultTitle: 'Task Checklist & Implementation Plan', imp: 'Essential', summary: 'Line-by-line task execution checklist with line numbers and file links.' },
            { doc: trdDoc, defaultTitle: 'Technical Requirements (TRD)', imp: 'Recommended', summary: 'API endpoint contracts, data schemas, and error response models.' },
            { doc: archDoc, defaultTitle: 'System Architecture Overview', imp: 'Reference', summary: 'Component file layout, state management stores, and worker thread RPC protocols.' }
          ], pkg.documents)
        };
    }
  }

  private static buildRecommendedDocs(
    slots: { doc?: SpecDocumentNode | undefined; defaultTitle: string; imp: 'Essential' | 'Recommended' | 'Reference'; summary: string }[],
    allDocs: SpecDocumentNode[]
  ): RecommendedDoc[] {
    const list: RecommendedDoc[] = [];

    slots.forEach(slot => {
      if (slot.doc) {
        list.push({
          path: slot.doc.path,
          title: slot.doc.meta?.title || slot.doc.name,
          importance: slot.imp,
          plainSummary: slot.summary
        });
      }
    });

    // Fill remaining documents from package if fewer than 3 match
    allDocs.forEach(d => {
      if (!list.some(item => item.path === d.path) && list.length < 5) {
        list.push({
          path: d.path,
          title: d.meta?.title || d.name,
          importance: 'Reference',
          plainSummary: `Specification document defining key details for ${d.name}.`
        });
      }
    });

    return list;
  }
}
